import { useEffect, useEffectEvent } from 'react';
import { ErrorLike } from '@apollo/client';
import { skipToken, useQuery } from '@apollo/client/react';
import { useLocation, useNavigate } from 'react-router';

import {
    BudgetRequestDocument,
    CurrentUserDocument,
    IBudgetRequestStatus,
    IContentTypeValue,
} from 'graphql/types';
import { Loader } from 'common/components/Loader';
import { NotFound } from 'common/components/NotFound';
import { PageDrawer } from 'common/components/PageDrawer';
import { useApolloError } from 'common/hooks/useApolloError';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { getUrl } from 'route/utils/getUrl';
import { usePermission } from 'user/hooks/usePermissions';
import { BudgetRequestDetail } from 'budget/components/BudgetRequestDetailDrawer/BudgetRequestDetail';
import { BudgetRequestFormController } from 'budget/forms';

interface IProps {
    open?: boolean;
}

export const BudgetRequestManageDrawer = ({ open }: IProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showApolloError } = useApolloError();

    const editMatch = useRouteMatch<{ budgetRequestId: string }>(
        'BUDGET_REQUEST_EDIT'
    );

    const budgetRequestId = editMatch?.params.budgetRequestId;

    const { data: currentUserData } = useQuery(CurrentUserDocument);

    const userId = currentUserData?.currentUser?.id;

    const isEdit = !!budgetRequestId;

    const { data, loading, error } = useQuery(
        BudgetRequestDocument,
        isEdit ? { variables: { id: budgetRequestId } } : skipToken
    );

    const { hasPermission: canEdit, loading: loadingCanEdit } = usePermission({
        category: 'edit',
        contentType: IContentTypeValue.BudgetRequest,
        targetId: budgetRequestId,
    });

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const budgetRequest = data?.budgetRequest;

    const budgetRequestNotFound = isEdit && !loading && !budgetRequest;

    // Once a manager has picked it up the request can no longer be changed,
    // so it is shown read-only. A new request is always a form.
    const showForm =
        !budgetRequest ||
        (canEdit &&
            budgetRequest.status === IBudgetRequestStatus.PendingManager);

    const isLoading = loading || (isEdit && loadingCanEdit);

    const handleClose = () => {
        navigate(`${getUrl('PORTFOLIO_BUDGET')}${location.search}`);
    };

    const handleCreate = (newBudgetRequestId: string) => {
        navigate(
            `${getUrl('BUDGET_REQUEST_EDIT', {
                budgetRequestId: newBudgetRequestId,
            })}${location.search}`
        );
    };

    return (
        <PageDrawer
            closeOnBackdropClick={false}
            open={open}
            onClose={handleClose}
        >
            {isLoading && <Loader />}

            {budgetRequestNotFound && <NotFound />}

            {!isLoading &&
                !budgetRequestNotFound &&
                !!userId &&
                (showForm ? (
                    <BudgetRequestFormController
                        budgetRequest={budgetRequest}
                        userId={userId}
                        onClose={handleClose}
                        onCreate={handleCreate}
                    />
                ) : (
                    <BudgetRequestDetail budgetRequest={budgetRequest} />
                ))}
        </PageDrawer>
    );
};
